// Comprehensive site-data wipe.
//
// When a user signs out, we want the next user (or the same user next time
// they sign in) to land on a clean slate — no cached data, no stale session
// cookies, no Dexie rows from the previous account, no service-worker code
// running against old auth context.
//
// Kondo's `clearSiteData` covers the same ground. The steps below mirror
// that shape so we behave the same way across both apps when switching
// accounts on a shared device.
//
// Called from `signOut` (auth.tsx) for the strong case. Also exposed as
// a standalone utility for admin / "reset app" flows.
//
// ADR: docs/adr/0020-clear-site-data.md

import { createLogger } from './logger'

const log = createLogger('clear-site-data')

export interface ClearSiteDataOptions {
  /** Call location.reload() after wiping. Default true. */
  reload?: boolean
}

/**
 * Nuke every client-side data surface we touch. Resolves when all steps
 * either succeed or error; errors during one step don't prevent the rest
 * from running. If `reload: true` (default), triggers a location.reload
 * at the end — callers that want to persist some UI state past the wipe
 * can pass `reload: false`.
 *
 * Steps, in order:
 *   1. localStorage — full wipe
 *   2. sessionStorage — full wipe
 *   3. Cookies — each on document.cookie (best-effort; httpOnly cookies
 *      can't be cleared from JS and must be handled server-side)
 *   4. IndexedDB — every database listed via `databases()` is deleted
 *   5. Cache Storage — every cache under caches.keys()
 *   6. Service worker — unregister all registrations for this origin
 *   7. Reload — so a clean page mounts against the wiped state
 *
 * Each step is wrapped in try/catch so a single browser-specific
 * restriction (private mode, CSP, cross-origin weirdness) doesn't abort
 * the rest.
 */
export async function clearSiteData(opts: ClearSiteDataOptions = {}): Promise<void> {
  const { reload = true } = opts
  const started = Date.now()

  // 1. localStorage — synchronous
  try {
    localStorage.clear()
  } catch (e) {
    log.warn('localStorage.clear failed', e)
  }

  // 2. sessionStorage — synchronous
  try {
    sessionStorage.clear()
  } catch (e) {
    log.warn('sessionStorage.clear failed', e)
  }

  // 3. Cookies. document.cookie only lists non-httpOnly cookies. We set
  // each with Max-Age=0 to mark expired. Works for the current path and
  // domain; cookies set with a specific Domain / Path attribute may
  // survive (that's a browser limitation, not something we can work
  // around from JS).
  try {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const name = cookie.split('=')[0]?.trim()
      if (!name) continue
      // Expire for current path + parent paths + current host.
      document.cookie = `${name}=; Max-Age=0; path=/`
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${location.hostname}`
    }
  } catch (e) {
    log.warn('cookie expire failed', e)
  }

  // 4. IndexedDB — `databases()` is Chrome / Firefox / Edge. Safari < 17
  // doesn't expose it. Falls through silently on unsupported browsers;
  // the sign-out path still wipes localStorage + cookies which covers
  // the auth-session leak (the important case).
  try {
    if (typeof indexedDB !== 'undefined' && typeof (indexedDB as IDBFactoryWithEnumeration).databases === 'function') {
      const dbs = await (indexedDB as IDBFactoryWithEnumeration).databases!()
      await Promise.all(
        dbs
          .map((d) => d.name)
          .filter((n): n is string => typeof n === 'string')
          .map((name) =>
            new Promise<void>((resolve) => {
              const req = indexedDB.deleteDatabase(name)
              req.onsuccess = () => resolve()
              req.onerror = () => resolve() // swallow, keep going
              req.onblocked = () => resolve() // another tab holding — skip
            }),
          ),
      )
    }
  } catch (e) {
    log.warn('indexedDB wipe failed', e)
  }

  // 5. Cache Storage — used by service workers for their resource cache.
  // Safe to nuke; the SW will re-populate on next load.
  try {
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k)))
    }
  } catch (e) {
    log.warn('Cache Storage wipe failed', e)
  }

  // 6. Service workers — unregister so a fresh one can install from
  // the new auth context on reload.
  try {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister()))
    }
  } catch (e) {
    log.warn('service worker unregister failed', e)
  }

  log.info(`site data cleared in ${Date.now() - started}ms`)

  // 7. Reload. location.replace discards the current entry so back-
  // button doesn't return to a half-wiped page.
  if (reload) {
    try {
      location.replace('/')
    } catch {
      // ancient browser fallback
      location.href = '/'
    }
  }
}

// ─── Internal ────────────────────────────────────────────────────────────────

// Chrome / Firefox / Edge expose `indexedDB.databases()`; TypeScript's
// lib.dom.d.ts now declares it as non-optional but older runtimes (or
// stubbed environments in tests) may not implement it. We keep the
// optional marker at the call site via runtime check; here we just
// avoid widening/narrowing the base lib type.
type IDBFactoryWithEnumeration = IDBFactory
